import {useMemo, useState} from "react";
import {useApp} from "../../app.context";
import {useIntegrator} from "../../integrator.context";
import ImagePicker from "./imagePicker.view";
import {RemoveImageFromPackCommand} from "../../commands/removeImageFromPack.command";
import {makeImageLabelResolver} from "../../utils/imageLabels";

interface SlotRef {
    level: number;
    variation: number;
    currentKey?: string;
}

function ImagePackView() {
    const {state, executeCommand} = useIntegrator();
    const {imagePackId, setImagePackId, getLocalizationValue, getImageUrl, confirmDelete, setNotification} = useApp();
    const [pickerSlot, setPickerSlot] = useState<SlotRef | undefined>(undefined);
    const [hoveredSlot, setHoveredSlot] = useState<{level: number; variation: number} | undefined>(undefined);

    const resolver = useMemo(
        () => makeImageLabelResolver(state, getLocalizationValue),
        [state, getLocalizationValue]
    );

    async function handleRemove(level: number, variation: number, key: string) {
        if (!(await confirmDelete(resolver.labelFor(key).label, 'image'))) return;
        // Pre-check: if this is the sole variation of a non-trailing level,
        // refuse upfront with a notification so the user knows why.
        const pack = state.imagePacks[id];
        if (pack && pack[level]?.length === 1 && level !== pack.length - 1) {
            setNotification({
                title: "Can't remove this slot",
                message: `Removing the only image at Level ${level} would leave an empty level in the middle of the progression, which the runtime can't handle. Add another image at Level ${level} first, or remove higher levels first.`,
            });
            return;
        }
        executeCommand(new RemoveImageFromPackCommand(id, level, variation));
    }

    const id = imagePackId ?? '';
    const images = state.imagePacks[id];

    // Buildings start at runtime Level 1, so column 0 of their image pack is
    // never displayed in-game. Hide it from the editor so designers don't
    // waste effort on it. See AddBuildingCommand for the seeded dead slot.
    const isBuildingPack = state.buildings.some(b => b.id === id);
    const isUnitPack = state.units.some(u => u.id === id);
    // Multi-level packs (buildings + units — both can level up). Tiles and
    // resources only ever render pack[0] in-game, so a "next level"
    // placeholder would be a UI trap.
    const isLevelablePack = isBuildingPack || isUnitPack;
    const firstVisibleColumn = isBuildingPack ? 1 : 0;

    function constructPack(): string[][] {
        const pack: string[][] = [];

        for (let i = 0; i < images.length; i++) {
            pack.push([]);

            const variations = images[i];
            for (let j = 0; j < variations.length; j++) {
                pack[i].push(variations[j]);
            }

            pack[i].push(constructNextKey(id, i, variations.length))
        }

        // "Add a new level" placeholder column — only for entities whose
        // runtime can read pack[level>0]. Tiles and resources always render
        // pack[0] in-game, so a multi-level UI for them would silently
        // accept uploads that are never displayed.
        if (isLevelablePack) {
            pack.push([constructNextKey(id, images.length, 0)]);
        }
        return pack;
    }

    function constructNextKey(key: string, level: number, variation: number): string {
        return `${key}_${level}_${variation}`;
    }

    return (
        <div className={'layout vertical overlay'} onClick={() => {
            setImagePackId(undefined);
        }}>
            <div className={'layout vertical gap box'} style={{padding: 10, gap: 20, width: 400, maxHeight: 400, overflow: 'auto'}}
                 onClick={e => e.stopPropagation()}
            >
                <p className={'text header'}>{getLocalizationValue(id)}</p>
                <div>
                    <p>Horizontal: Progression</p>
                    <p>Vertical: Variations</p>
                </div>
                <div className={'layout horizontal gap'}>
                    {constructPack().map((levels, i) => {
                        if (i < firstVisibleColumn) return null;
                        return (
                            <div key={i} className={'layout vertical gap'}>
                                {isLevelablePack && <p style={{fontSize: 12, color: 'rgba(255,255,255,0.66)'}}>Level {i}</p>}
                                {levels.map((variation, j) => {
                                    // Slots inside the existing pack carry the
                                    // currently-assigned key as `variation`.
                                    // Trailing "new" placeholder slots carry a
                                    // derived key that isn't yet in state.images
                                    // — for those, currentKey stays undefined.
                                    const isExisting = j < (images[i]?.length ?? 0);
                                    const isHovered = hoveredSlot?.level === i && hoveredSlot?.variation === j;
                                    return (
                                        <div key={j}
                                             onClick={() => setPickerSlot({level: i, variation: j, currentKey: isExisting ? variation : undefined})}
                                             onMouseEnter={() => isExisting && setHoveredSlot({level: i, variation: j})}
                                             onMouseLeave={() => setHoveredSlot(undefined)}
                                             style={{
                                                 cursor: 'pointer',
                                                 position: 'relative',
                                                 width: 60, height: 60,
                                                 borderRadius: 5,
                                                 backgroundColor: 'black',
                                                 border: 'solid 1px #515151',
                                             }}>
                                            <img alt={variation} src={getImageUrl(variation)}
                                                 style={{width: '100%', height: '100%', borderRadius: 5}}/>
                                            {isExisting && isHovered && (
                                                <button
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        handleRemove(i, j, variation);
                                                    }}
                                                    title={'Remove this image from the pack'}
                                                    style={{
                                                        position: 'absolute', top: -6, right: -6,
                                                        width: 18, height: 18, padding: 0,
                                                        borderRadius: '50%', border: 'solid 1px #515151',
                                                        background: 'var(--background-color)',
                                                        color: 'white', cursor: 'pointer',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    }}>
                                                    <span className={'fa-solid fa-xmark'} style={{fontSize: 10}}/>
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )
                    })}
                </div>
            </div>
            {pickerSlot && (
                <ImagePicker packId={id} level={pickerSlot.level} variation={pickerSlot.variation}
                             currentKey={pickerSlot.currentKey}
                             onClose={() => setPickerSlot(undefined)}/>
            )}
        </div>
    )
}

export default ImagePackView;